import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { paintingAPI, requestAPI, orderAPI, auctionAPI, blogAPI } from '../utils/api';
import config from '../config/config';
import './AdminPanel.css';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const AdminPanel = () => {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('paintings');
  const [paintings, setPaintings] = useState([]);
  const [requests, setRequests] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [imageFiles, setImageFiles] = useState([]); // Updated to handle multiple images
  const [videos, setVideos] = useState([]); // New state for videos
  const [auctions, setAuctions] = useState([]);
  const [showAuctionModal, setShowAuctionModal] = useState(false);
  const [editing, setEditing] = useState(false); // New state to track edit mode
  const [editingPaintingId, setEditingPaintingId] = useState(null); // Track the painting being edited
  const [auctionFormData, setAuctionFormData] = useState({
    painting_id: '',
    starting_price: '',
    end_date: '',
  });

  // Blog states
  const [blogPosts, setBlogPosts] = useState([]);
  const [showBlogModal, setShowBlogModal] = useState(false);
  const [editingBlog, setEditingBlog] = useState(false);
  const [editingBlogId, setEditingBlogId] = useState(null);
  const [blogImageFile, setBlogImageFile] = useState(null);
  const [blogFormData, setBlogFormData] = useState({
    title: '',
    excerpt: '',
    content: '',
    category: '',
    tags: '',
    author: 'Aanishka Chowdhury',
    status: 'published',
    featured: false,
    meta_description: '',
    meta_keywords: '',
  });
  const contentTextareaRef = useRef(null);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    price: '',
    size: '',
    year: new Date().getFullYear(),
    is_auction: false,
    featured: false,
    meta_description: '',
    meta_keywords: '',
  });
  const [imageFile, setImageFile] = useState(null);

  // Blog handlers
  const handleBlogImageUpload = (e) => {
    setBlogImageFile(e.target.files[0]);
  };

  const resetBlogForm = () => {
    setBlogFormData({
      title: '',
      excerpt: '',
      content: '',
      category: '',
      tags: '',
      author: 'Aanishka Chowdhury',
      status: 'published',
      featured: false,
      meta_description: '',
      meta_keywords: '',
    });
    setBlogImageFile(null);
    setEditingBlog(false);
    setEditingBlogId(null);
  };

  // Text formatting helpers
  const wrapSelection = (before, after) => {
    const textarea = contentTextareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = blogFormData.content;

    if (start === end) {
      // No selection - insert placeholder
      const placeholder = 'text';
      const newText = text.substring(0, start) + before + placeholder + after + text.substring(end);
      setBlogFormData({ ...blogFormData, content: newText });
      setTimeout(() => {
        textarea.focus();
        const selectStart = start + before.length;
        const selectEnd = selectStart + placeholder.length;
        textarea.setSelectionRange(selectStart, selectEnd);
      }, 0);
    } else {
      // Wrap selected text
      const selected = text.substring(start, end);
      const newText = text.substring(0, start) + before + selected + after + text.substring(end);
      setBlogFormData({ ...blogFormData, content: newText });
      setTimeout(() => {
        textarea.focus();
        const newEnd = start + before.length + selected.length + after.length;
        textarea.setSelectionRange(start, newEnd);
      }, 0);
    }
  };

  const insertListItem = () => {
    const textarea = contentTextareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const text = blogFormData.content;
    const beforeCursor = text.substring(0, start);
    const afterCursor = text.substring(start);

    // Check if we need a newline before
    const needsNewline = beforeCursor.length > 0 && !beforeCursor.endsWith('\n');
    const prefix = needsNewline ? '\n- ' : '- ';

    const newText = beforeCursor + prefix + afterCursor;
    setBlogFormData({ ...blogFormData, content: newText });

    setTimeout(() => {
      textarea.focus();
      const newCursorPos = start + prefix.length;
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  };

  const fetchBlogPosts = async () => {
    try {
      const response = await blogAPI.getAllAdmin();
      setBlogPosts(response.data);
    } catch (error) {
      console.error('Error fetching blog posts:', error);
    }
  };

  const handleSaveBlog = async (e) => {
    e.preventDefault();
    const data = new FormData();
    Object.keys(blogFormData).forEach((key) => {
      data.append(key, blogFormData[key]);
    });
    if (blogImageFile) {
      data.append('image', blogImageFile);
    }

    try {
      if (editingBlog) {
        await blogAPI.update(editingBlogId, data);
        toast.success('Blog post updated successfully!');
      } else {
        await blogAPI.create(data);
        toast.success('Blog post created successfully!');
      }
      setShowBlogModal(false);
      resetBlogForm();
      fetchBlogPosts();
    } catch (error) {
      toast.error('Error saving blog post. Please try again.');
    }
  };

  const handleDeleteBlog = async (id) => {
    if (window.confirm('Are you sure you want to delete this blog post?')) {
      try {
        await blogAPI.delete(id);
        toast.success('Blog post deleted!');
        fetchBlogPosts();
      } catch (error) {
        toast.error('Error deleting blog post');
      }
    }
  };

  const handleEditBlog = (post) => {
    setBlogFormData({
      title: post.title,
      excerpt: post.excerpt || '',
      content: post.content,
      category: post.category || '',
      tags: post.tags?.join(', ') || '',
      author: post.author,
      status: post.status,
      featured: post.featured,
      meta_description: post.meta_description || '',
      meta_keywords: post.meta_keywords || '',
    });
    setBlogImageFile(null);
    setEditingBlog(true);
    setEditingBlogId(post.id);
    setShowBlogModal(true);
  };

  const handleVideoUpload = (e) => {
    const files = Array.from(e.target.files);
    const newVideos = files.map((file, index) => ({
      id: Date.now() + index,
      file,
      name: file.name,
    }));
    setVideos((prevVideos) => [...prevVideos, ...newVideos]);
  };
  
  const handleVideoDelete = (id) => {
    setVideos((prevVideos) => prevVideos.filter((video) => video.id !== id));
  };
  
  const handleImageUpload = (e) => {
    setImageFiles(Array.from(e.target.files));
  };

  // AI Analysis
  const [analyzing, setAnalyzing] = useState(false);

  // Smart local suggestion generator based on filename (no API needed)
  const generateFallbackSuggestions = (filename) => {
    // Clean the filename
    let cleanName = filename
      .replace(/^aanishka-chowdhury-/, '')
      .replace(/\d{10,}/, '')
      .replace(/[-_]/g, ' ')
      .replace(/\.[^.]+$/, '')
      .trim();

    // Extract meaningful words (ignore very short ones)
    const words = cleanName.split(/\s+/).filter(w => w.length > 2);
    
    // Build title from filename words
    let title = words.length > 0
      ? words.map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
      : 'Original Hand-Painted Canvas Art';

    // Detect category from filename keywords
    const nameLower = cleanName.toLowerCase();
    let category = 'Abstract'; // default
    
    if (nameLower.includes('flower') || nameLower.includes('rose') || nameLower.includes('bloom')) category = 'Floral';
    else if (nameLower.includes('mountain') || nameLower.includes('hill') || nameLower.includes('sunset') || nameLower.includes('river') || nameLower.includes('sky')) category = 'Landscape';
    else if (nameLower.includes('face') || nameLower.includes('woman') || nameLower.includes('man') || nameLower.includes('lady') || nameLower.includes('girl') || nameLower.includes('boy')) category = 'Portrait';
    else if (nameLower.includes('tree') || nameLower.includes('forest') || nameLower.includes('leaf') || nameLower.includes('garden')) category = 'Nature';
    else if (nameLower.includes('god') || nameLower.includes('buddha') || nameLower.includes('temple') || nameLower.includes('spiritual') || nameLower.includes('meditation')) category = 'Spiritual';
    else if (nameLower.includes('city') || nameLower.includes('street') || nameLower.includes('building') || nameLower.includes('urban')) category = 'Modern';
    else if (nameLower.includes('animal') || nameLower.includes('bird') || nameLower.includes('elephant') || nameLower.includes('horse')) category = 'Nature';

    return {
      title: title || 'Original Hand-Painted Canvas Art',
      description: `A stunning ${category.toLowerCase()} painting meticulously hand-painted on high-quality canvas by artist Aanishka Chowdhury. This original artwork brings warmth, character, and artistic elegance to any living space. Perfect for home decor, office interiors, or as a thoughtful gift for art lovers.`,
      category: category,
      meta_description: `Buy original ${category.toLowerCase()} painting "${title}" by Aanishka Chowdhury. Hand-painted canvas art for home decor. Unique artwork available exclusively on Canvaart.`,
      meta_keywords: `original painting, ${category.toLowerCase()} art, canvas painting, hand painted, Aanishka Chowdhury, home decor, wall art, living room art, buy painting online, Indian artist`,
    };
  };

  const handleAnalyzeWithAI = async () => {
    if (imageFiles.length === 0) {
      toast.warning('Please upload at least one image first');
      return;
    }

    setAnalyzing(true);

    try {
      const data = new FormData();
      data.append('image', imageFiles[0]);

      const response = await paintingAPI.analyze(data);

      if (response?.data?.success && response?.data?.data) {
        const aiData = response.data.data;

        setFormData({
          ...formData,
          title: aiData.title || formData.title,
          description: aiData.description || formData.description,
          category: aiData.category || formData.category,
          meta_description: aiData.meta_description || formData.meta_description,
          meta_keywords: aiData.keywords || formData.meta_keywords,
        });

        toast.success('AI analysis complete! Fields auto-filled from image content.', { autoClose: 5000 });
      } else {
        throw new Error('Invalid AI response');
      }
    } catch (error) {
      console.error('AI analysis error:', error);

      // Fallback on any error
      toast.warning('AI service busy. Using Smart Suggest fallback...', { autoClose: 3000 });

      const fallback = generateFallbackSuggestions(imageFiles[0].name);
      setFormData({
        ...formData,
        title: fallback.title,
        description: fallback.description,
        category: fallback.category,
        meta_description: fallback.meta_description,
        meta_keywords: fallback.meta_keywords,
      });

      toast.info('Smart Suggestions applied! Review and edit.', { autoClose: 5000 });
    } finally {
      setAnalyzing(false);
    }
  };

  const handleEditPainting = (painting) => {
    setFormData({
      title: painting.title,
      description: painting.description,
      category: painting.category,
      price: painting.price,
      size: painting.size,
      year: painting.year,
      is_auction: painting.is_auction,
      featured: painting.featured,
      meta_description: painting.meta_description || '',
      meta_keywords: painting.meta_keywords || '',
    });
    setImageFiles([]); // Clear image files for editing
    setVideos([]); // Clear videos for editing
    setEditing(true); // Set editing mode
    setEditingPaintingId(painting.id); // Store the painting ID
    setShowAddModal(true); // Open the modal for editing
  };

  useEffect(() => {
    if (!isAdmin()) {
      navigate('/login');
      return;
    }
    fetchData();
  }, [activeTab, isAdmin, navigate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'paintings') {
        const response = await paintingAPI.getAll();
        setPaintings(response.data);
      } else if (activeTab === 'requests') {
        const response = await requestAPI.getAll();
        setRequests(response.data);
      } else if (activeTab === 'orders') {
        const response = await orderAPI.getAll();
        setOrders(response.data);
      } else if (activeTab === 'auctions') {
        fetchAuctions();
      } else if (activeTab === 'blog') {
        fetchBlogPosts();
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddPainting = async (e) => {
    e.preventDefault();
    const data = new FormData();
    Object.keys(formData).forEach((key) => {
      data.append(key, formData[key]);
    });
  
    // Append multiple images
    imageFiles.forEach((file) => {
      data.append(`images`, file);
    });
  
    // Append sorted videos
    videos.forEach((video) => {
      data.append(`videos`, video.file);
    });
  
    try {
      if (editing) {
        // Update existing painting
        await paintingAPI.update(editingPaintingId, data); // Call the update API
        toast.success('Painting updated successfully!');
      } else {
        // Add new painting
        await paintingAPI.create(data); // Call the create API
        toast.success('Painting added successfully!');
      }
      setShowAddModal(false);
      fetchData(); // Refresh the data
      resetForm();
    } catch (error) {
      toast.error('Error saving painting. Please try again.');
    }
  };

  const handleDeletePainting = async (id) => {
    if (window.confirm('Are you sure you want to delete this painting?')) {
      try {
        await paintingAPI.delete(id);
        fetchData();
      } catch (error) {
        alert('Error deleting painting');
      }
    }
  };

  const handleUpdateRequestStatus = async (id, status) => {
    try {
      await requestAPI.updateStatus(id, status);
      fetchData();
    } catch (error) {
      alert('Error updating request status');
    }
  };

  const handleDeleteRequest = async (id) => {
    if (window.confirm('Are you sure you want to delete this request?')) {
      try {
        await requestAPI.delete(id);
        fetchData();
      } catch (error) {
        alert('Error deleting request');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      category: '',
      price: '',
      size: '',
      year: new Date().getFullYear(),
      is_auction: false,
      featured: false,
      meta_description: '',
      meta_keywords: '',
    });
    setImageFiles([]);
    setVideos([]);
    setEditing(false); // Reset editing mode
    setEditingPaintingId(null); // Clear the editing painting ID
  };

  const fetchAuctions = async () => {
    try {
      const response = await auctionAPI.getAll();
      setAuctions(response.data);
    } catch (error) {
      console.error('Error fetching auctions:', error);
    }
  };

  const handleCreateAuction = async (e) => {
    e.preventDefault();
    try {
      await auctionAPI.create(auctionFormData);
      setShowAuctionModal(false);
      fetchAuctions();
    } catch (error) {
      console.error('Error creating auction:', error);
    }
  };

  const handleDeleteAuction = async (id) => {
    if (window.confirm('Are you sure you want to delete this auction?')) {
      try {
        await auctionAPI.delete(id);
        fetchAuctions();
      } catch (error) {
        console.error('Error deleting auction:', error);
      }
    }
  };

  return (
    <div className="admin-panel">
      <ToastContainer />
      <div className="admin-header">
        <h1>Admin Panel</h1>
        <p>Manage your paintings, requests, and orders</p>
      </div>

      <div className="admin-tabs">
        <button
          className={activeTab === 'paintings' ? 'active' : ''}
          onClick={() => setActiveTab('paintings')}
        >
          Paintings
        </button>
        <button
          className={activeTab === 'auctions' ? 'active' : ''}
          onClick={() => setActiveTab('auctions')}
        >
          Auctions
        </button>
        <button
          className={activeTab === 'requests' ? 'active' : ''}
          onClick={() => setActiveTab('requests')}
        >
          Custom Requests
        </button>
        <button
          className={activeTab === 'orders' ? 'active' : ''}
          onClick={() => setActiveTab('orders')}
        >
          Orders
        </button>
        <button
          className={activeTab === 'blog' ? 'active' : ''}
          onClick={() => setActiveTab('blog')}
        >
          Blog
        </button>
      </div>

      <div className="admin-content">
        {activeTab === 'paintings' && (
          <div className="paintings-section">
            <div className="section-header">
              <h2>Manage Paintings</h2>
              <button className="btn-primary" onClick={() => setShowAddModal(true)}>
                + Add New Painting
              </button>
            </div>

            {loading ? (
              <div className="spinner"></div>
            ) : (
              <div className="admin-table">
                <table>
                  <thead>
                    <tr>
                      <th>Image</th>
                      <th>Title</th>
                      <th>Category</th>
                      <th>Price</th>
                      <th>Status</th>
                      <th>Featured</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paintings.map(painting => (
                      <tr key={painting.id}>
                        <td>
                          <img
                            src={`${config.BASE_API_URL}${painting.images[0]}`}
                            alt={painting.title}
                            className="table-img"
                          />
                        </td>
                        <td>{painting.title}</td>
                        <td>{painting.category}</td>
                        <td>₹{painting.price}</td>
                        <td><span className={`status ${painting.status}`}>{painting.status}</span></td>
                        <td>{painting.featured ? '⭐' : '-'}</td>
                        <td>
                          <button
                            className="btn-edit"
                            onClick={() => handleEditPainting(painting)}
                          >
                            Edit
                          </button>
                          <button
                            className="btn-delete"
                            onClick={() => handleDeletePainting(painting.id)}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )} 

        {activeTab === 'requests' && (
          <div className="requests-section">
            <h2>Custom Requests</h2>
            {loading ? (
              <div className="spinner"></div>
            ) : (
              <div className="requests-grid">
                {requests.map(request => (
                  <div key={request.id} className="request-card">
                    <div className="request-header">
                      <h3>{request.name}</h3>
                      <span className={`status ${request.status}`}>{request.status}</span>
                    </div>
                    <p><strong>Email:</strong> {request.email}</p>
                    <p><strong>Phone:</strong> {request.phone}</p>
                    <p><strong>Description:</strong> {request.description}</p>
                    <p><strong>Budget:</strong> ₹{request.budget}</p>
                    <p><strong>Timeline:</strong> {request.timeline}</p>
                    
                    {request.reference_image && (
                      <>
                      <p><strong>Reference Image:</strong> </p>
                      <img src={`${config.BASE_API_URL}${request.reference_image}`} alt="Reference" className="request-image" />
                      </>
                    )}
                    <div className="request-actions">
                      <select
                        value={request.status}
                        onChange={(e) => handleUpdateRequestStatus(request.id, e.target.value)}
                      >
                        <option value="pending">Pending</option>
                        <option value="in-progress">In Progress</option>
                        <option value="completed">Completed</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                      <strong> / </strong>
                      <button
                        className="btn-delete"
                        onClick={() => handleDeleteRequest(request.id)}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'orders' && (
          <div className="orders-section">
            <h2>Orders</h2>
            {loading ? (
              <div className="spinner"></div>
            ) : (
              <div className="admin-table">
                <table>
                  <thead>
                    <tr>
                      <th>Order ID</th>
                      <th>Painting</th>
                      <th>Buyer</th>
                      <th>Email</th>
                      <th>Amount</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map(order => (
                      <tr key={order.id}>
                        <td>#{order.id}</td>
                        <td>{order.painting_title || 'N/A'}</td>
                        <td>{order.buyer_name}</td>
                        <td>{order.buyer_email}</td>
                        <td>₹{order.total_amount}</td>
                        <td><span className={`status ${order.order_status}`}>{order.order_status}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

{activeTab === 'auctions' && (
          <div className="auctions-section">
            <div className="section-header">
              <h2>Manage Auctions</h2>
              <button className="btn-primary" onClick={() => setShowAuctionModal(true)}>
                + Add New Auction
              </button>
            </div>

            <div className="admin-table">
              <table>
                <thead>
                  <tr>
                    <th>Painting</th>
                    <th>Starting Price</th>
                    <th>Highest Bid</th>
                    <th>End Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {auctions.map((auction) => (
                    <tr key={auction.id}>
                      <td>
                        {auction.title}
                        <br />
                        <img
                          src={`${config.BASE_API_URL}${auction.images[0]}`}
                          alt={auction.title}
                          className="table-img"
                        />
                      </td>
                      <td>₹{auction.starting_price}</td>
                      <td>₹{auction.highest_bid || 'N/A'}</td>
                      <td>{new Date(auction.end_date).toLocaleString()}</td>
                      <td>
                        <button
                          className="btn-delete"
                          onClick={() => handleDeleteAuction(auction.id)}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'blog' && (
          <div className="blog-section">
            <div className="section-header">
              <h2>Manage Blog Posts</h2>
              <button className="btn-primary" onClick={() => { resetBlogForm(); setShowBlogModal(true); }}>
                + Add New Post
              </button>
            </div>

            {loading ? (
              <div className="spinner"></div>
            ) : (
              <div className="admin-table">
                <table>
                  <thead>
                    <tr>
                      <th>Image</th>
                      <th>Title</th>
                      <th>Category</th>
                      <th>Status</th>
                      <th>Featured</th>
                      <th>Date</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {blogPosts.map((post) => (
                      <tr key={post.id}>
                        <td>
                          {post.image_url && (
                            <img
                              src={`${config.BASE_API_URL}${post.image_url}`}
                              alt={post.title}
                              className="table-img"
                            />
                          )}
                        </td>
                        <td>{post.title}</td>
                        <td>{post.category || '-'}</td>
                        <td>
                          <span className={`status ${post.status}`}>{post.status}</span>
                        </td>
                        <td>{post.featured ? '⭐' : '-'}</td>
                        <td>{new Date(post.created_at).toLocaleDateString()}</td>
                        <td>
                          <button
                            className="btn-edit"
                            onClick={() => handleEditBlog(post)}
                          >
                            Edit
                          </button>
                          <button
                            className="btn-delete"
                            onClick={() => handleDeleteBlog(post.id)}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Add Painting Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Add New Painting</h2>
            <form onSubmit={handleAddPainting}>
              <div className="form-group">
                <label>Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows="3"
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Category *</label>
                  <input
                    type="text"
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Price *</label>
                  <input
                    type="number"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    required
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Size</label>
                  <input
                    type="text"
                    value={formData.size}
                    onChange={(e) => setFormData({ ...formData, size: e.target.value })}
                    placeholder="e.g., 24x36 inches"
                  />
                </div>
                <div className="form-group">
                  <label>Year</label>
                  <input
                    type="number"
                    value={formData.year}
                    onChange={(e) => setFormData({ ...formData, year: e.target.value })}
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Images {editing ? '' : '*'}</label>
                <input
                  type="file"
                  onChange={handleImageUpload}
                  accept="image/*"
                  multiple
                  required={!editing}
                />
                <div className="image-preview">
                  {imageFiles.map((file, index) => (
                    <span key={index}>{file.name}</span>
                  ))}
                </div>
                {editing && (
                  <small className="edit-hint">
                    Leave empty to keep existing images. Upload new images only if you want to replace them.
                  </small>
                )}
                {imageFiles.length > 0 && (
                  <button
                    type="button"
                    className="btn-ai-analyze"
                    onClick={handleAnalyzeWithAI}
                    disabled={analyzing}
                  >
                    {analyzing ? (
                      <>
                        <span className="spinner-mini" /> AI Analyzing...
                      </>
                    ) : (
                      <>
                        🤖 AI Auto-Fill (GPT-4o)
                      </>
                    )}
                  </button>
                )}
              </div>

              <div className="form-group">
                <label>Videos</label>
                <input
                  type="file"
                  onChange={handleVideoUpload}
                  accept="video/*"
                  multiple
                />
                <div className="video-list">
                  {videos.map((video, index) => (
                    <div key={video.id} className="video-item">
                      <span>{video.name}</span>
                      <button
                        type="button"
                        onClick={() => handleVideoDelete(video.id)}
                      >
                        Delete
                      </button>
                    </div>
                  ))}
                </div>
              </div>
              <div className="form-group">
                <label>Meta Description (SEO)</label>
                <textarea
                  value={formData.meta_description}
                  onChange={(e) => setFormData({ ...formData, meta_description: e.target.value })}
                  rows="2"
                  placeholder="150-160 character description for search engines..."
                />
              </div>
              <div className="form-group">
                <label>Meta Keywords (SEO)</label>
                <input
                  type="text"
                  value={formData.meta_keywords}
                  onChange={(e) => setFormData({ ...formData, meta_keywords: e.target.value })}
                  placeholder="canvas art, abstract painting, home decor, wall art"
                />
              </div>
              <div className="form-checkboxes">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.is_auction}
                    onChange={(e) => setFormData({ ...formData, is_auction: e.target.checked })}
                  />
                  Auction Item
                </label>
                <label>
                  <input
                    type="checkbox"
                    checked={formData.featured}
                    onChange={(e) => setFormData({ ...formData, featured: e.target.checked })}
                  />
                  Featured
                </label>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowAddModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  Add Painting
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Auction Modal */}
      {showAuctionModal && (
        <div className="modal-overlay" onClick={() => setShowAuctionModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Add New Auction</h2>
            <form onSubmit={handleCreateAuction}>
              <div className="form-group">
                <label>Painting ID *</label>
                <input
                  type="text"
                  value={auctionFormData.painting_id}
                  onChange={(e) =>
                    setAuctionFormData({ ...auctionFormData, painting_id: e.target.value })
                  }
                  required
                />
              </div>
              <div className="form-group">
                <label>Starting Price *</label>
                <input
                  type="number"
                  value={auctionFormData.starting_price}
                  onChange={(e) =>
                    setAuctionFormData({ ...auctionFormData, starting_price: e.target.value })
                  }
                  required
                />
              </div>
              <div className="form-group">
                <label>End Date *</label>
                <input
                  type="datetime-local"
                  value={auctionFormData.end_date}
                  onChange={(e) =>
                    setAuctionFormData({ ...auctionFormData, end_date: e.target.value })
                  }
                  required
                />
              </div>
              <div className="modal-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setShowAuctionModal(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  Add Auction
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Blog Post Modal */}
      {showBlogModal && (
        <div className="modal-overlay" onClick={() => setShowBlogModal(false)}>
          <div className="modal-content blog-modal" onClick={(e) => e.stopPropagation()}>
            <h2>{editingBlog ? 'Edit Blog Post' : 'Add New Blog Post'}</h2>
            <form onSubmit={handleSaveBlog}>
              <div className="form-group">
                <label>Title *</label>
                <input
                  type="text"
                  value={blogFormData.title}
                  onChange={(e) => setBlogFormData({ ...blogFormData, title: e.target.value })}
                  required
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Category</label>
                  <input
                    type="text"
                    value={blogFormData.category}
                    onChange={(e) => setBlogFormData({ ...blogFormData, category: e.target.value })}
                    placeholder="e.g., Home Decor, Art Tips"
                  />
                </div>
                <div className="form-group">
                  <label>Author</label>
                  <input
                    type="text"
                    value={blogFormData.author}
                    onChange={(e) => setBlogFormData({ ...blogFormData, author: e.target.value })}
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Excerpt (short summary)</label>
                <textarea
                  value={blogFormData.excerpt}
                  onChange={(e) => setBlogFormData({ ...blogFormData, excerpt: e.target.value })}
                  rows="2"
                  placeholder="A brief summary that appears on the blog listing page..."
                />
              </div>
              <div className="form-group">
                <label>Content *</label>
                <div className="formatting-toolbar">
                  <button
                    type="button"
                    className="toolbar-btn bold-btn"
                    onClick={() => wrapSelection('**', '**')}
                    title="Bold"
                  >
                    <b>B</b>
                  </button>
                  <button
                    type="button"
                    className="toolbar-btn italic-btn"
                    onClick={() => wrapSelection('_', '_')}
                    title="Italic"
                  >
                    <i>I</i>
                  </button>
                  <button
                    type="button"
                    className="toolbar-btn list-btn"
                    onClick={insertListItem}
                    title="List Item"
                  >
                    &#8226; List
                  </button>
                </div>
                <div className="formatting-help">
                  <small>
                    Select text and click buttons above, or type <b>**bold**</b> <i>_italic_</i> <code>- list</code>
                  </small>
                </div>
                <textarea
                  ref={contentTextareaRef}
                  className="content-textarea"
                  value={blogFormData.content}
                  onChange={(e) => setBlogFormData({ ...blogFormData, content: e.target.value })}
                  rows="12"
                  required
                  placeholder="Write your article here...&#10;&#10;Select text and click the Bold or Italic buttons above.&#10;&#10;Create lists by clicking the List button.&#10;&#10;Leave a blank line between paragraphs."
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Tags (comma separated)</label>
                  <input
                    type="text"
                    value={blogFormData.tags}
                    onChange={(e) => setBlogFormData({ ...blogFormData, tags: e.target.value })}
                    placeholder="canvas art, abstract, home decor"
                  />
                </div>
                <div className="form-group">
                  <label>Status</label>
                  <select
                    value={blogFormData.status}
                    onChange={(e) => setBlogFormData({ ...blogFormData, status: e.target.value })}
                  >
                    <option value="published">Published</option>
                    <option value="draft">Draft</option>
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label>Featured Image</label>
                <input
                  type="file"
                  onChange={handleBlogImageUpload}
                  accept="image/*"
                />
                {blogImageFile && <span className="file-name">{blogImageFile.name}</span>}
              </div>
              <div className="form-group">
                <label>Meta Description (SEO)</label>
                <textarea
                  value={blogFormData.meta_description}
                  onChange={(e) => setBlogFormData({ ...blogFormData, meta_description: e.target.value })}
                  rows="2"
                  placeholder="Brief description for search engines (150-160 chars)"
                />
              </div>
              <div className="form-group">
                <label>Meta Keywords (SEO)</label>
                <input
                  type="text"
                  value={blogFormData.meta_keywords}
                  onChange={(e) => setBlogFormData({ ...blogFormData, meta_keywords: e.target.value })}
                  placeholder="comma, separated, keywords"
                />
              </div>
              <div className="form-checkboxes">
                <label>
                  <input
                    type="checkbox"
                    checked={blogFormData.featured}
                    onChange={(e) => setBlogFormData({ ...blogFormData, featured: e.target.checked })}
                  />
                  Featured Post (appears on hero)
                </label>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowBlogModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  {editingBlog ? 'Update Post' : 'Publish Post'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPanel;
